import _slicedToArray from "/home/runner/work/notes/notes/.layouts/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/home/runner/work/notes/notes/.layouts/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["children"];
import React from 'react';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import defaultTheme from './theme';
import deepmerge from 'deepmerge';
var defaultColorMode = 'day';
var defaultDayScheme = 'light';
var defaultNightScheme = 'dark'; // eslint-disable-next-line @typescript-eslint/no-explicit-any

var ThemeContext = /*#__PURE__*/React.createContext({
  setColorMode: function setColorMode() {
    return null;
  },
  setDayScheme: function setDayScheme() {
    return null;
  },
  setNightScheme: function setNightScheme() {
    return null;
  }
});
export var ThemeProvider = function ThemeProvider(_ref8) {
  var children = _ref8.children,
      props = _objectWithoutProperties(_ref8, _excluded);

  var _ref, _props$theme, _ref2, _props$colorMode, _ref3, _props$dayScheme, _ref4, _props$nightScheme; // Get fallback values from parent ThemeProvider (if exists)


  var _useTheme = useTheme(),
      fallbackTheme = _useTheme.theme,
      fallbackColorMode = _useTheme.colorMode,
      fallbackDayScheme = _useTheme.dayScheme,
      fallbackNightScheme = _useTheme.nightScheme; // Initialize state


  var theme = (_ref = (_props$theme = props.theme) !== null && _props$theme !== void 0 ? _props$theme : fallbackTheme) !== null && _ref !== void 0 ? _ref : defaultTheme;

  var _React$useState = React.useState((_ref2 = (_props$colorMode = props.colorMode) !== null && _props$colorMode !== void 0 ? _props$colorMode : fallbackColorMode) !== null && _ref2 !== void 0 ? _ref2 : defaultColorMode),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      colorMode = _React$useState2[0],
      setColorMode = _React$useState2[1];

  var _React$useState3 = React.useState((_ref3 = (_props$dayScheme = props.dayScheme) !== null && _props$dayScheme !== void 0 ? _props$dayScheme : fallbackDayScheme) !== null && _ref3 !== void 0 ? _ref3 : defaultDayScheme),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      dayScheme = _React$useState4[0],
      setDayScheme = _React$useState4[1];

  var _React$useState5 = React.useState((_ref4 = (_props$nightScheme = props.nightScheme) !== null && _props$nightScheme !== void 0 ? _props$nightScheme : fallbackNightScheme) !== null && _ref4 !== void 0 ? _ref4 : defaultNightScheme),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      nightScheme = _React$useState6[0],
      setNightScheme = _React$useState6[1];

  var systemColorMode = useSystemColorMode();
  var resolvedColorMode = resolveColorMode(colorMode, systemColorMode);
  var colorScheme = chooseColorScheme(resolvedColorMode, dayScheme, nightScheme);
  var resolvedTheme = React.useMemo(function () {
    return applyColorScheme(theme, colorScheme);
  }, [theme, colorScheme]); // Update state if props change

  React.useEffect(function () {
    var _ref5, _props$colorMode2;

    setColorMode((_ref5 = (_props$colorMode2 = props.colorMode) !== null && _props$colorMode2 !== void 0 ? _props$colorMode2 : fallbackColorMode) !== null && _ref5 !== void 0 ? _ref5 : defaultColorMode);
  }, [props.colorMode, fallbackColorMode]);
  React.useEffect(function () {
    var _ref6, _props$dayScheme2;

    setDayScheme((_ref6 = (_props$dayScheme2 = props.dayScheme) !== null && _props$dayScheme2 !== void 0 ? _props$dayScheme2 : fallbackDayScheme) !== null && _ref6 !== void 0 ? _ref6 : defaultDayScheme);
  }, [props.dayScheme, fallbackDayScheme]);
  React.useEffect(function () {
    var _ref7, _props$nightScheme2;

    setNightScheme((_ref7 = (_props$nightScheme2 = props.nightScheme) !== null && _props$nightScheme2 !== void 0 ? _props$nightScheme2 : fallbackNightScheme) !== null && _ref7 !== void 0 ? _ref7 : defaultNightScheme);
  }, [props.nightScheme, fallbackNightScheme]);
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: {
      theme: resolvedTheme,
      colorScheme: colorScheme,
      colorMode: colorMode,
      resolvedColorMode: resolvedColorMode,
      dayScheme: dayScheme,
      nightScheme: nightScheme,
      setColorMode: setColorMode,
      setDayScheme: setDayScheme,
      setNightScheme: setNightScheme
    }
  }, /*#__PURE__*/React.createElement(SCThemeProvider, {
    theme: resolvedTheme
  }, children));
};
ThemeProvider.displayName = "ThemeProvider";
export function useTheme() {
  return React.useContext(ThemeContext);
}
export function useColorSchemeVar(values, fallback) {
  var _values$colorScheme;

  var _useTheme2 = useTheme(),
      _useTheme2$colorSchem = _useTheme2.colorScheme,
      colorScheme = _useTheme2$colorSchem === void 0 ? '' : _useTheme2$colorSchem;

  return (_values$colorScheme = values[colorScheme]) !== null && _values$colorScheme !== void 0 ? _values$colorScheme : fallback;
}

function useSystemColorMode() {
  var _React$useState7 = React.useState(getSystemColorMode),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      systemColorMode = _React$useState8[0],
      setSystemColorMode = _React$useState8[1];

  React.useEffect(function () {
    var _window, _window$matchMedia; // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition


    var media = (_window = window) === null || _window === void 0 ? void 0 : (_window$matchMedia = _window.matchMedia) === null || _window$matchMedia === void 0 ? void 0 : _window$matchMedia.call(_window, '(prefers-color-scheme: dark)');

    function handleChange(event) {
      var isNight = event.matches;
      setSystemColorMode(isNight ? 'night' : 'day');
    } // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition


    if (media) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (media.addEventListener !== undefined) {
        media.addEventListener('change', handleChange);
        return function cleanup() {
          media.removeEventListener('change', handleChange);
        };
      } // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      else if (media.addListener !== undefined) {
        media.addListener(handleChange);
        return function cleanup() {
          media.removeListener(handleChange);
        };
      }
    }
  }, []);
  return systemColorMode;
}

function getSystemColorMode() {
  var _window$matchMedia2, _window2, _window$matchMedia2$c; // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition


  if (typeof window !== 'undefined' && (_window$matchMedia2 = (_window2 = window).matchMedia) !== null && _window$matchMedia2 !== void 0 && (_window$matchMedia2$c = _window$matchMedia2.call(_window2, '(prefers-color-scheme: dark)')) !== null && _window$matchMedia2$c !== void 0 && _window$matchMedia2$c.matches) {
    return 'night';
  }

  return 'day';
}

function resolveColorMode(colorMode, systemColorMode) {
  switch (colorMode) {
    case 'auto':
      return systemColorMode;

    default:
      return colorMode;
  }
}

function chooseColorScheme(colorMode, dayScheme, nightScheme) {
  switch (colorMode) {
    case 'day':
      return dayScheme;

    case 'night':
      return nightScheme;
  }
}

function applyColorScheme(theme, colorScheme) {
  if (!theme.colorSchemes) {
    return theme;
  }

  if (!theme.colorSchemes[colorScheme]) {
    // eslint-disable-next-line no-console
    console.error("`".concat(colorScheme, "` scheme not defined in `theme.colorSchemes`")); // Apply the first defined color scheme

    var defaultColorScheme = Object.keys(theme.colorSchemes)[0];
    return deepmerge(theme, theme.colorSchemes[defaultColorScheme]);
  }

  return deepmerge(theme, theme.colorSchemes[colorScheme]);
}

export default ThemeProvider;